import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiCompass,
  FiTrendingUp,
  FiGrid,
  FiX,
  FiUsers,
  FiStar,
  FiZap,
  FiArrowRight,
  FiGlobe,
  FiLock,
  FiUserPlus,
  FiClock,
  FiBookOpen,
  FiActivity,
  FiAward,
  FiChevronRight,
  FiLoader,
} from "react-icons/fi";
import {
  usePopularCommunities,
  useRecommendedCommunities,
  useCommunitiesByField,
} from "../../../services/communities/useDiscovery";
import { useTrendingCommunities as useTrendingHook } from "../../../services/communities/useTrendingCommunities";
import communityService from "../../../services/communityService";
import { canDirectJoin } from "../../../utils/communityPermissions";
import JoinRequestModal from "../JoinRequestModal";
import "./DiscoverySection.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.05,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 12, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const fadeVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.25 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

// View configuration
const DISCOVERY_VIEWS = [
  { id: "discover", label: "Discover", icon: FiCompass },
  { id: "trending", label: "Trending", icon: FiTrendingUp },
  { id: "browse", label: "Browse", icon: FiGrid },
];

// Research fields list
const RESEARCH_FIELDS = [
  "Computer Science",
  "Mathematics",
  "Physics",
  "Chemistry",
  "Biology",
  "Medicine",
  "Engineering",
  "Psychology",
  "Economics",
  "Philosophy",
  "Environmental Science",
  "Neuroscience",
  "Law",
  "Business",
  "Education",
];

/**
 * DiscoverySection - Modern community discovery interface
 */
const DiscoverySection = () => {
  const navigate = useNavigate();

  // View state
  const [activeView, setActiveView] = useState("discover");
  const [selectedField, setSelectedField] = useState(null);
  const [trendingTimeFilter, setTrendingTimeFilter] = useState("week");

  // Join modal state
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [pendingRequests, setPendingRequests] = useState(new Set());

  // Fetch data for different views
  const { data: recommendedData, isLoading: recommendedLoading } =
    useRecommendedCommunities(6);

  const { data: popularData, isLoading: popularLoading } =
    usePopularCommunities("members", 6);

  const { data: activeData, isLoading: activeLoading } = usePopularCommunities(
    "activity",
    6
  );



  const { data: trendingData, isLoading: trendingLoading } = useTrendingHook(
    trendingTimeFilter,
    12
  );

  const { data: fieldData, isLoading: fieldLoading } = useCommunitiesByField(
    selectedField,
    20,
    1
  );

  // Extract communities
  const recommendedCommunities = recommendedData?.communities || [];
  const popularCommunities = popularData?.communities || [];
  const activeCommunities = activeData?.communities || [];

  const trendingCommunities = trendingData || [];
  const fieldCommunities = fieldData?.communities || [];

  // Handle join request
  const handleJoinRequest = useCallback(async (community) => {
    try {
      if (canDirectJoin(community, community.userMembership)) {
        await communityService.directJoinCommunity(community._id);
        toast.success("Successfully joined the community!");
      } else {
        setSelectedCommunity(community);
        setShowJoinModal(true);
      }
    } catch (error) {
toast.error(error.message || "Failed to join community");
    }
  }, []);

  // Handle join request success
  const handleJoinRequestSuccess = useCallback(() => {
    if (selectedCommunity) {
      setPendingRequests((prev) => new Set([...prev, selectedCommunity._id]));
    }
  }, [selectedCommunity]);

  // Handle community click
  const handleCommunityClick = useCallback(
    (community) => {
      const isMember = community.userMembership?.role;
      if (isMember) {
        navigate(`/communities/${community._id}`);
      } else {
        navigate(`/communities/${community._id}/preview`);
      }
    },
    [navigate]
  );

  // Format member count
  const formatMemberCount = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count?.toString() || "0";
  };

  // Community Card Component
  const CommunityCard = ({ community, showRank = false, rank = 0 }) => {
    const isMember = community.userMembership?.role;
    const isPending = pendingRequests.has(community._id);

    return (
      <motion.div
        className="chub-discovery-card"
        variants={cardVariants}
        onClick={() => handleCommunityClick(community)}
        whileHover={{ y: -4 }}
      >
        {showRank && (
          <div className="chub-card-rank">
            <span>#{rank}</span>
          </div>
        )}

        <div className="chub-card-avatar">
          {community.signedPhotoURL || community.photoURL ? (
            <img
              src={community.signedPhotoURL || community.photoURL}
              alt={community.name}
            />
          ) : (
            <FiUsers size={20} />
          )}
        </div>

        <div className="chub-card-content">
          <div className="chub-card-header">
            <h4 className="chub-card-title">{community.name}</h4>
            <div className="chub-card-badges">
              <span className={`chub-card-type ${community.type}`}>
                {community.type === "public" ? (
                  <FiGlobe size={10} />
                ) : (
                  <FiLock size={10} />
                )}
              </span>
              {community.field && (
                <span className="chub-card-field">{community.field}</span>
              )}
            </div>
          </div>

          <p className="chub-card-description">
            {community.description?.slice(0, 80)}
            {community.description?.length > 80 ? "..." : ""}
          </p>

          <div className="chub-card-footer">
            <div className="chub-card-stats">
              <span className="chub-stat">
                <FiUsers size={12} />
                {formatMemberCount(community.memberCount)}
              </span>
              {community.growthRate && (
                <span className="chub-stat growth">
                  <FiTrendingUp size={12} />+{community.growthRate.toFixed(0)}%
                </span>
              )}
            </div>

            <div className="chub-card-actions">
              {isMember ? (
                <button className="chub-card-btn enter">
                  <FiArrowRight size={14} />
                </button>
              ) : isPending ? (
                <button className="chub-card-btn pending" disabled>
                  <FiClock size={14} />
                </button>
              ) : (
                <button
                  className="chub-card-btn join"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleJoinRequest(community);
                  }}
                >
                  <FiUserPlus size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // Section Component for Discover view
  const DiscoverySectionItem = ({
    title,
    icon: Icon,
    communities,
    loading,
    gradient,
  }) => {
    if (loading) {
      return (
        <div className="chub-section">
          <div className="chub-section-header">
            <div className="chub-section-icon" style={{ background: gradient }}>
              <Icon size={16} />
            </div>
            <h3>{title}</h3>
          </div>
          <div className="chub-section-loading">
            <FiLoader className="chub-animate-spin" size={24} />
          </div>
        </div>
      );
    }

    if (!communities || communities.length === 0) return null;

    return (
      <div className="chub-section">
        <div className="chub-section-header">
          <div className="chub-section-icon" style={{ background: gradient }}>
            <Icon size={16} />
          </div>
          <h3>{title}</h3>
          <button className="chub-section-more">
            View All <FiChevronRight size={14} />
          </button>
        </div>
        <motion.div
          className="chub-section-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {communities.slice(0, 6).map((community) => (
            <CommunityCard key={community._id} community={community} />
          ))}
        </motion.div>
      </div>
    );
  };

  // Render Discover View
  const renderDiscoverView = () => (
    <div className="chub-discover-view">
      {recommendedCommunities.length > 0 && (
        <DiscoverySectionItem
          title="Recommended for You"
          icon={FiStar}
          communities={recommendedCommunities}
          loading={recommendedLoading}
          gradient="var(--chub-gradient-warm)"
        />
      )}

      <DiscoverySectionItem
        title="Most Popular"
        icon={FiAward}
        communities={popularCommunities}
        loading={popularLoading}
        gradient="var(--chub-gradient-primary)"
      />

      <DiscoverySectionItem
        title="Most Active"
        icon={FiActivity}
        communities={activeCommunities}
        loading={activeLoading}
        gradient="var(--chub-gradient-fresh)"
      />
    </div>
  );

  // Render Trending View
  const renderTrendingView = () => (
    <div className="chub-trending-view">
      <div className="chub-trending-header">
        <div className="chub-trending-title">
          <FiZap className="chub-trending-icon" size={20} />
          <div>
            <h2>Trending Communities</h2>
            <p>Fast-growing communities based on recent activity</p>
          </div>
        </div>
        <div className="chub-trending-filters">
          {["day", "week", "month"].map((period) => (
            <button
              key={period}
              className={`chub-period-btn ${
                trendingTimeFilter === period ? "active" : ""
              }`}
              onClick={() => setTrendingTimeFilter(period)}
            >
              {period === "day"
                ? "Today"
                : period === "week"
                ? "This Week"
                : "This Month"}
            </button>
          ))}
        </div>
      </div>

      {trendingLoading ? (
        <div className="chub-loading-state">
          <FiLoader className="chub-animate-spin" size={32} />
          <p>Loading trending communities...</p>
        </div>
      ) : trendingCommunities.length > 0 ? (
        <motion.div
          className="chub-trending-list"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {trendingCommunities.map((community, index) => (
            <CommunityCard
              key={community._id}
              community={community}
              showRank={true}
              rank={index + 1}
            />
          ))}
        </motion.div>
      ) : (
        <div className="chub-empty-state">
          <FiTrendingUp size={40} />
          <h3>No trending communities</h3>
          <p>Check back later for trending communities</p>
        </div>
      )}
    </div>
  );

  // Render Browse View
  const renderBrowseView = () => (
    <div className="chub-browse-view">
      <div className="chub-browse-header">
        <h2>Browse by Field</h2>
        <p>Explore communities in your area of interest</p>
      </div>

      <div className="chub-fields-grid">
        {RESEARCH_FIELDS.map((field) => (
          <button
            key={field}
            className={`chub-field-btn ${
              selectedField === field ? "active" : ""
            }`}
            onClick={() =>
              setSelectedField(selectedField === field ? null : field)
            }
          >
            <FiBookOpen size={14} />
            {field}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {selectedField && (
          <motion.div
            className="chub-field-results"
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="chub-results-header">
              <h3>{selectedField} Communities</h3>
              <button
                className="chub-clear-field"
                onClick={() => setSelectedField(null)}
              >
                <FiX size={14} />
                Clear
              </button>
            </div>

            {fieldLoading ? (
              <div className="chub-loading-state compact">
                <FiLoader className="chub-animate-spin" size={24} />
              </div>
            ) : fieldCommunities.length > 0 ? (
              <motion.div
                className="chub-results-grid"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {fieldCommunities.map((community) => (
                  <CommunityCard key={community._id} community={community} />
                ))}
              </motion.div>
            ) : (
              <div className="chub-empty-state compact">
                <p>No communities found in {selectedField}</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );



  return (
    <div className="chub-discovery">
      {/* View Switcher */}
      <div className="chub-discovery-nav">
        {DISCOVERY_VIEWS.map((view) => {
          const Icon = view.icon;
          const isActive = activeView === view.id;

          return (
            <motion.button
              key={view.id}
              className={`chub-view-btn ${isActive ? "active" : ""}`}
              onClick={() => setActiveView(view.id)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Icon size={16} />
              <span>{view.label}</span>
              {isActive && (
                <motion.div
                  className="chub-view-indicator"
                  layoutId="activeView"
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 30,
                  }}
                />
              )}
            </motion.button>
          );
        })}
      </div>

      {/* View Content */}
      <div className="chub-discovery-content">
        <AnimatePresence mode="wait">
          {activeView === "discover" && (
            <motion.div
              key="discover"
              variants={fadeVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {renderDiscoverView()}
            </motion.div>
          )}
          {activeView === "trending" && (
            <motion.div
              key="trending"
              variants={fadeVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {renderTrendingView()}
            </motion.div>
          )}
          {activeView === "browse" && (
            <motion.div
              key="browse"
              variants={fadeVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {renderBrowseView()}
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      {/* Join Request Modal */}
      <AnimatePresence>
        {showJoinModal && (
          <JoinRequestModal
            isOpen={showJoinModal}
            onClose={() => {
              setShowJoinModal(false);
              setSelectedCommunity(null);
            }}
            community={selectedCommunity}
            onSuccess={handleJoinRequestSuccess}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DiscoverySection;
